/**
 * How a message describes itself when there is no room for the message.
 *
 * Channel lists, push notifications and quoted replies all need one line
 * standing in for a whole message, and each surface used to invent its own.
 * They disagreed: the sidebar showed the upload's filename (`📎
 * voice-note-1755274839123.m4a`), the notification for the same message said
 * "Sent a voice note", and a quote of it showed nothing at all. The names are
 * generated — nobody typed them and nobody should read them.
 */
/** How long a preview may be before it is cut — a sidebar row's worth. */
export const PREVIEW_LENGTH = 140;
/**
 * A clip's length as `m:ss`, or '' when nothing measured it.
 *
 * Rounded rather than truncated: a 13.6-second note reading "0:13" undersells
 * every clip by up to a second, and the number is only ever an at-a-glance
 * "is this worth listening to now" — the player shows the real position.
 */
export function formatClipLength(durationMs) {
    if (!durationMs || !Number.isFinite(durationMs) || durationMs < 0)
        return '';
    const total = Math.round(durationMs / 1000);
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}
/** ` (0:14)`, or '' — the parenthesised tail the previews below share. */
function clipLengthSuffix(attachment) {
    const clock = formatClipLength(attachment.durationMs);
    return clock ? ` (${clock})` : '';
}
/**
 * What an attachment *is*, as an object phrase: "a voice note", "an image".
 * Reads as the tail of "Sent …" and of "Ahmed sent …".
 */
export function describeAttachment(attachment) {
    const mime = attachment.mimeType ?? '';
    if (attachment.kind === 'voice' || mime.startsWith('audio/'))
        return `a voice note${clipLengthSuffix(attachment)}`;
    if (attachment.kind === 'gif')
        return 'a GIF';
    if (attachment.kind === 'sticker')
        return 'a sticker';
    if (mime.startsWith('image/'))
        return 'an image';
    if (mime.startsWith('video/'))
        return 'a video';
    if (mime === 'application/pdf' || /\.pdf$/i.test(attachment.name ?? ''))
        return 'a PDF';
    return 'an attachment';
}
/**
 * Names the clients make up when the source has none: a recording, a camera
 * capture, or a picture the photo roll handed over without a filename.
 * `voice-note-1755274839123.m4a`, `photo-1755274839123-0.jpg`.
 */
const GENERATED_NAME = /^(voice-note|image|photo|video)-\d{10,}(-\d+)?\.[a-z0-9]+$/i;
/** Whether a name was invented by the app rather than chosen by a person. */
export function isGeneratedName(name) {
    return !name || GENERATED_NAME.test(name);
}
/**
 * What to call an attachment on its own, capitalised, with no article:
 * a heading for the thing itself rather than a sentence about it.
 *
 * A file keeps its own name — someone chose it, and "Q3-budget.xlsx" is the
 * whole point of sending it. A voice note, GIF or sticker never has one worth
 * reading, and neither does a photo the roll handed over unnamed: showing
 * `photo-1755274839123-0.jpg` tells the reader nothing the picture beside it
 * does not already say. A voice note carries its length instead, which is the
 * one thing a reader wants before deciding to play it.
 */
export function attachmentLabel(attachment) {
    const mime = attachment.mimeType ?? '';
    if (attachment.kind === 'voice' || mime.startsWith('audio/'))
        return `Voice note${clipLengthSuffix(attachment)}`;
    if (attachment.kind === 'gif')
        return 'GIF';
    if (attachment.kind === 'sticker')
        return 'Sticker';
    if (!isGeneratedName(attachment.name))
        return attachment.name;
    if (mime.startsWith('image/'))
        return 'Photo';
    if (mime.startsWith('video/'))
        return 'Video';
    return 'Attachment';
}
/**
 * One line standing in for a message, in the third person: what the channel
 * list and the quoted-reply card show.
 *
 * A body always wins — an image with a caption is the caption. Everything
 * else describes what was sent.
 */
export function messagePreview(message) {
    const body = message.body?.trim();
    if (body)
        return body.slice(0, PREVIEW_LENGTH);
    if (message.poll?.question)
        return `Started a poll: ${message.poll.question}`.slice(0, PREVIEW_LENGTH);
    const attachments = message.attachments ?? [];
    if (attachments.length === 1)
        return `Sent ${describeAttachment(attachments[0])}`;
    if (attachments.length > 1)
        return `Sent ${attachments.length} attachments`;
    return '';
}
